/**
 * i18n.t vs Destructured t: While both provide translation
 * functionalities, i18n.t ensures consistency with your
 * configured instance and is generally safer for maintaining
 * reactivity and configuration.
 *
 * Best Practice: Use the i18n instance (i18n.t) when needing
 * translations outside React components. For React components,
 * prefer the useTranslation hook to handle reactivity seamlessly.
 */
import i18n from 'i18next';
import { ComponentProps, forwardRef, useState } from 'react';
import InputNumber from 'antd/es/input-number';
import clsx from 'clsx';

import { OpSelect } from '../OpSelect/OpSelect';

import './OpInputDuration.scss';

interface OpInputDurationProps
  extends ComponentProps<typeof InputNumber<number>> {
  testId?: string;
  defaultUnitValue?: number;
  unitOptions?: { value: number; label: string }[];
  minSeconds?: number;
  maxSeconds?: number;
}

const defaultUnitOptions = [
  { value: 1, label: i18n.t('Seconds') },
  { value: 60, label: i18n.t('Minutes') },
];

export const OpInputDuration = forwardRef<
  HTMLInputElement,
  OpInputDurationProps
>(
  (
    {
      className,
      testId = 'op-duration-input',
      defaultUnitValue = defaultUnitOptions[0].value,
      unitOptions = defaultUnitOptions,
      minSeconds,
      maxSeconds,
      value: propsValue,
      onChange: propsOnChange,
      ...inputNumberProps
    },
    ref,
  ) => {
    const [unit, setUnit] = useState(defaultUnitValue);

    const handleUnitChange = (value: number) => {
      setUnit(value);
    };

    const handleValueChange = (value: number | null) => {
      propsOnChange?.(value && unit ? value * unit : null);
    };

    const displayValue = propsValue && unit ? propsValue / unit : propsValue;

    return (
      <InputNumber
        className={clsx('op-input-duration', className)}
        ref={ref}
        data-testid={testId}
        min={0}
        {...inputNumberProps}
        addonAfter={
          unitOptions.length > 1 ? (
            <OpSelect
              className={clsx('op-input-duration__select', className)}
              options={unitOptions}
              value={unit}
              onChange={handleUnitChange}
            />
          ) : (
            unitOptions[0].label
          )
        }
        value={displayValue}
        onChange={handleValueChange}
        {...(minSeconds && { min: Math.floor(minSeconds / unit) })}
        {...(maxSeconds && { max: maxSeconds / unit })}
      />
    );
  },
);
